/**
 * AbmahnungModal.jsx – Abmahnung erstellen und archivieren
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Loader2, FileText, AlertTriangle, Download, CheckCircle2, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ABMAHNUNG_GRUENDE = [
  'Unpünktlichkeit / unentschuldigtes Fehlen',
  'Kassen-Differenz / Fehlbetrag',
  'Arbeitsverweigerung',
  'Fehlverhalten gegenüber Kunden',
  'Fehlverhalten gegenüber Kollegen / Vorgesetzten',
  'Verstoß gegen Hygienevorschriften (HACCP)',
  'Verstoß gegen Sicherheitsvorschriften',
  'Unerlaubtes Verlassen des Arbeitsplatzes',
  'Verstoß gegen Datenschutz / Verschwiegenheitspflicht',
  'Sonstiger Pflichtverstoß',
];

export function AbmahnungModal({ open, onClose, employee, bogen, locations = [] }) {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    grund: '',
    vorfall_beschreibung: '',
    vorfall_datum: new Date().toISOString().slice(0, 10),
  });
  const [firmaData, setFirmaData] = useState({
    firma_name: '', firma_adresse: '', firma_plz_ort: '', firma_unterzeichner: '', standort: '',
  });
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1); setSavedId(null);
    const ag = bogen?.arbeitgeber || {};
    // Auto-select from employee location
    const locId = (employee?.location_ids || [])[0] || ag.location_id || bogen?.location_id;
    if (locId) {
      const loc = locations.find(l => l.id === locId);
      if (loc) {
        setFirmaData({
          firma_name: loc.legal_name || loc.name || '',
          firma_adresse: loc.legal_address || '',
          firma_plz_ort: loc.legal_plz_ort || loc.city || '',
          firma_unterzeichner: loc.legal_unterzeichner || loc.legal_owner || '',
          standort: loc.city || loc.name || '',
        });
      }
    }
  }, [open, employee, bogen, locations]);

  const handleSave = async () => {
    if (!form.grund || !form.vorfall_beschreibung) {
      toast.error('Bitte Grund und Beschreibung ausfüllen'); return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/abmahnung`, {
        employee_id: employee?.id,
        mitarbeiter_name: employee?.name || '',
        eintrittsdatum: bogen?.arbeitgeber?.eintrittsdatum || '',
        ...firmaData,
        ...form,
        vorfall_datum: form.vorfall_datum
          ? new Date(form.vorfall_datum).toLocaleDateString('de-DE') : '',
        beschaeftigung_art: bogen?.arbeitgeber?.arbeitszeit || 'Vollzeit',
      }, { headers });
      setSavedId(res.data.id);
      setStep(2);
      toast.success('Abmahnung gespeichert!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally { setSaving(false); }
  };

  const handleDownload = async () => {
    if (!savedId) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/api/abmahnung/${savedId}/pdf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Abmahnung_${employee?.name?.replace(/ /g, '_') || 'Mitarbeiter'}.pdf`;
      a.click();
    } catch { toast.error('PDF-Fehler'); }
    finally { setDownloading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Abmahnung erstellen
            {employee?.name && <span className="text-sm font-normal text-muted-foreground ml-1">– {employee.name}</span>}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-2">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
              <p className="text-xs text-red-700 dark:text-red-300 font-medium">
                Abmahnungen werden in der Personalakte archiviert und können bei einer späteren Kündigung als Nachweis dienen.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Grund der Abmahnung *</Label>
              <select value={form.grund} onChange={e => setForm(f => ({...f, grund: e.target.value}))}
                className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                data-testid="abmahnung-grund-select">
                <option value="">Bitte wählen…</option>
                {ABMAHNUNG_GRUENDE.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Datum des Vorfalls *</Label>
              <Input type="date" value={form.vorfall_datum}
                onChange={e => setForm(f => ({...f, vorfall_datum: e.target.value}))}
                className="h-9" data-testid="abmahnung-datum" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Genaue Beschreibung des Vorfalls *</Label>
              <textarea value={form.vorfall_beschreibung}
                onChange={e => setForm(f => ({...f, vorfall_beschreibung: e.target.value}))}
                rows={5} placeholder="Beschreibe den Vorfall möglichst präzise mit Datum, Uhrzeit und Ort…"
                className="w-full rounded-xl border bg-background px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                data-testid="abmahnung-beschreibung" />
              <p className="text-xs text-muted-foreground">Je präziser, desto rechtssicherer. Angaben wie Uhrzeit und Zeugen stärken die Abmahnung.</p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-2">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-xl p-4 text-center space-y-2">
              <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto" />
              <p className="font-semibold text-green-800 dark:text-green-300">Abmahnung gespeichert!</p>
              <p className="text-xs text-green-700 dark:text-green-400">In der Personalakte von {employee?.name} archiviert.</p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-xl p-3">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Das PDF muss ausgedruckt, handschriftlich unterschrieben und dem Mitarbeiter persönlich übergeben werden.
                Lassen Sie sich den Empfang quittieren (Empfangsbestätigung im Dokument).
              </p>
            </div>
            <Button onClick={handleDownload} disabled={downloading} className="w-full gap-2 bg-red-600 hover:bg-red-700"
              data-testid="download-abmahnung-btn">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Abmahnung PDF herunterladen
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {step === 2 ? 'Schließen' : 'Abbrechen'}
          </Button>
          {step === 1 && (
            <Button onClick={handleSave} disabled={saving || !form.grund || !form.vorfall_beschreibung}
              className="gap-2 bg-red-600 hover:bg-red-700" data-testid="save-abmahnung-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Abmahnung speichern
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
